import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getApi } from '../../../Services/apiService';

const UserManagement = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [viewingUserId, setViewingUserId] = useState(null);
    const [viewUserData, setViewUserData] = useState(null);
    const [viewLoader, setViewLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        role: '',
        isActive: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [loader, setLoader] = useState(false);
    
    // API state
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [take] = useState(10);
    const [search, setSearch] = useState('');
    const [totalUsers, setTotalUsers] = useState(0);
    const [petOwnerCount, setPetOwnerCount] = useState(0);
    const [communityHeroCount, setCommunityHeroCount] = useState(0);
    const [bystanderCount, setBystanderCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
    const [pagination, setPagination] = useState({
        page: 1,
        take: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const handleView = (id) => {
        navigate(`/admin/users/${id}`);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setViewingUserId(null);
        setViewUserData(null);
    };

    const handleEdit = (id) => {
        const user = users.find(u => u.id === id);
        if (user) {
            setEditingUserId(id);
            const roleOrType = user.role ?? user.userType ?? user.type ?? '';
            setFormData({
                name: user.name ?? user.fullName ?? '',
                phone: user.phone ?? '',
                email: user.email ?? '',
                address: user.address ?? user.city ?? '',
                role: roleOrType,
                isActive: user.isActive !== undefined ? user.isActive : (user.status?.toLowerCase() === 'active')
            });
            setFormErrors({});
            setIsEditModalOpen(true);
        }
    };

    const handleDelete = (id) => {
        setDeletingUserId(id);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeletingUserId(null);
    };

    const confirmDelete = async () => {
        if (!deletingUserId) return;
        
        setDeleteLoader(true);
        try {
            // TODO: Implement delete user API call
            toast.info('Delete functionality coming soon');
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user. Please try again.');
        } finally {
            setDeleteLoader(false);
        }
    };

    // Fetch users from API
    // API response: { statusCode, message, data: { counts, users, pagination } }
    // For filter: send ?status=Active or ?status=Inactive; for All send no status param. Pagination: page, take.
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all') {
                params.set('status', statusFilter === 'active' ? 'Active' : 'Inactive');
            }
            params.set('page', String(page));
            params.set('take', String(take));
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await getApi(`/api/v1/admin/users${query}`);
            const data = response?.data ?? {};
            const listArray = Array.isArray(data.users) ? data.users : [];
            const counts = data.counts ?? {};
            const pag = data.pagination ?? {};

            setUsers(listArray);
            setTotalUsers(counts.totalUsers ?? listArray.length);
            setPetOwnerCount(counts.petOwners ?? 0);
            setCommunityHeroCount(counts.communityHeroes ?? 0);
            setBystanderCount(counts.bystanders ?? 0);
            setPagination({
                page: pag.page ?? page,
                take: pag.take ?? take,
                totalItems: pag.totalItems ?? 0,
                totalPages: pag.totalPages ?? 0,
                hasNextPage: Boolean(pag.hasNextPage),
                hasPreviousPage: Boolean(pag.hasPreviousPage)
            });
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
            setTotalUsers(0);
            setPetOwnerCount(0);
            setCommunityHeroCount(0);
            setBystanderCount(0);
            setPagination({ page: 1, take, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search, statusFilter]);

    const handleAddUser = () => {
        setIsModalOpen(true);
    };

    const handleExport = () => {
        toast.info('Export functionality coming soon');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditModalOpen(false);
        setEditingUserId(null);
        setFormData({
            name: '',
            phone: '',
            email: '',
            address: '',
            role: '',
            isActive: true
        });
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'User name is required';
        }
        
        if (!formData.email.trim()) {
            errors.email = 'Email ID is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement add user API call
            toast.info('Add user functionality coming soon');
            closeModal();
            fetchUsers();
        } catch (error) {
            console.error('Error adding user:', error);
            toast.error('Failed to add user. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // TODO: Implement update user API call
            toast.info('Update user functionality coming soon');
            closeModal();
            fetchUsers();
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Failed to update user. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className='RightSideDiv users-management'>
            {/* Statistics Cards - match screenshot 2 */}
            <div className="dashboard-stats">
                <div className="stat-card">
                    <h2 className="stat-number">{totalUsers}</h2>
                    <h3 className="stat-label">Total Users</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">{petOwnerCount}</h2>
                    <h3 className="stat-label">Pet owner</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">{communityHeroCount}</h2>
                    <h3 className="stat-label">Total community Hero</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">{bystanderCount}</h2>
                    <h3 className="stat-label">Total Bystander</h3>
                </div>
            </div>

            {/* All Users Section with status filter */}
            <div className="shelters-header">
                <h2>All Users</h2>
                <div className="shelters-header-actions">
                    <select
                        className="users-filter-select"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        aria-label="Filter by status"
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>

            <div className="shelters-section">
                <div className="table-outer">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>Loading users...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>No users found.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table user-management-table">
                                <thead>
                                    <tr className="t-heading">
                                        <th><h5>Sr. no.</h5></th>
                                        <th><h5>Name</h5></th>
                                        <th><h5>Type</h5></th>
                                        <th><h5>Date joined</h5></th>
                                        <th><h5>City</h5></th>
                                        <th><h5>Rank</h5></th>
                                        <th><h5>No. of pet</h5></th>
                                        <th><h5>Status</h5></th>
                                        <th><h5>Action</h5></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => {
                                        const dateJoined = user.dateJoined ?? user.createdAt ?? user.date_joined;
                                        const dateStr = dateJoined
                                            ? (typeof dateJoined === 'string' && !dateJoined.match(/^\d{4}-\d{2}-\d{2}/)
                                                ? dateJoined
                                                : new Date(dateJoined).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }))
                                            : '-';
                                        const isActive = user.status?.toLowerCase() === 'active' || user.isActive === true;
                                        return (
                                            <tr key={user.id}>
                                                <td><h5>{user.srNo ?? '-'}</h5></td>
                                                <td><h5>{user.name ?? user.fullName ?? '-'}</h5></td>
                                                <td><h5>{user.type ?? user.userType ?? user.role ?? '-'}</h5></td>
                                                <td><h5>{dateStr}</h5></td>
                                                <td><h5>{user.city ?? user.address ?? '-'}</h5></td>
                                                <td><h5>{user.rank ?? '-'}</h5></td>
                                                <td><h5>{user.numberOfPets ?? user.petCount ?? user.noOfPet ?? '-'}</h5></td>
                                                <td>
                                                    <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                                                        {user.status ?? (isActive ? 'Active' : 'Inactive')}
                                                    </span>
                                                </td>
                                                <td className="action">
                                                    <button
                                                        className="view-detail-btn"
                                                        onClick={() => handleView(user.id)}
                                                        title="View Detail"
                                                    >
                                                        View Detail
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Pagination - show when we have multiple pages or items */}
                {!loading && pagination.totalPages > 0 && (
                    <div className="pagination-bar">
                        <div className="pagination-controls">
                            <button
                                type="button"
                                className="pagination-btn pagination-prev-next"
                                disabled={!pagination.hasPreviousPage}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                aria-label="Previous page"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="pagination-pages">
                                {(() => {
                                    const { page: currentPage, totalPages } = pagination;
                                    const pages = [];
                                    const showEllipsisStart = totalPages > 5 && currentPage > 3;
                                    const showEllipsisEnd = totalPages > 5 && currentPage < totalPages - 2;
                                    let start = 1;
                                    let end = totalPages;
                                    if (totalPages > 5) {
                                        if (currentPage <= 3) {
                                            start = 1;
                                            end = 4;
                                        } else if (currentPage >= totalPages - 2) {
                                            start = totalPages - 3;
                                            end = totalPages;
                                        } else {
                                            start = currentPage - 1;
                                            end = currentPage + 1;
                                        }
                                    }
                                    if (showEllipsisStart) pages.push(1, 'ellipsis-start');
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    if (showEllipsisEnd && end < totalPages) pages.push('ellipsis-end', totalPages);
                                    return pages.map((item, idx) =>
                                        item === 'ellipsis-start' || item === 'ellipsis-end' ? (
                                            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                                        ) : (
                                            <button
                                                key={item}
                                                type="button"
                                                className={`pagination-btn pagination-page ${item === currentPage ? 'active' : ''}`}
                                                onClick={() => setPage(item)}
                                                aria-label={`Page ${item}`}
                                                aria-current={item === currentPage ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        )
                                    );
                                })()}
                            </div>
                            <button
                                type="button"
                                className="pagination-btn pagination-prev-next"
                                disabled={!pagination.hasNextPage}
                                onClick={() => setPage((p) => p + 1)}
                                aria-label="Next page"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add User Modal */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Add User Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Add User</h2>
                        <p>User basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="name">User name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter user name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="phone">Phone number</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    className={formErrors.phone ? 'error' : ''}
                                />
                                {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="email">Email ID <span className="required">*</span></label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email ID"
                                    className={formErrors.email ? 'error' : ''}
                                />
                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="address">Address</label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Enter address"
                                className={formErrors.address ? 'error' : ''}
                            />
                            {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="role">Role</label>
                            <select
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                className={formErrors.role ? 'error' : ''}
                            >
                                <option value="">Select role</option>
                                <option value="User">User</option>
                                <option value="Admin">Admin</option>
                            </select>
                            {formErrors.role && <span className="error-message">{formErrors.role}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Adding...' : 'Add User'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit User Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={closeModal}
                contentLabel="Edit User Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Edit User</h2>
                        <p>User basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleUpdateSubmit}>
                    <div className="form-content">
                        <div className="form-group">
                            <label htmlFor="edit-name">User name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="edit-name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter user name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-phone">Phone number</label>
                                <input
                                    type="tel"
                                    id="edit-phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    className={formErrors.phone ? 'error' : ''}
                                />
                                {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-email">Email ID <span className="required">*</span></label>
                                <input
                                    type="email"
                                    id="edit-email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email ID"
                                    className={formErrors.email ? 'error' : ''}
                                />
                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-address">Address</label>
                            <input
                                type="text"
                                id="edit-address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Enter address"
                                className={formErrors.address ? 'error' : ''}
                            />
                            {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-role">Role</label>
                            <select
                                id="edit-role"
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                className={formErrors.role ? 'error' : ''}
                            >
                                <option value="">Select role</option>
                                <option value="User">User</option>
                                <option value="Admin">Admin</option>
                            </select>
                            {formErrors.role && <span className="error-message">{formErrors.role}</span>}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Updating...' : 'Update User'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                contentLabel="Delete Confirmation Modal"
                className="delete-confirmation-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="delete-modal-header">
                    <button className="close-btn" onClick={closeDeleteModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="delete-modal-content">
                    <div className="delete-warning-icon">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="#f44336"/>
                            <path d="M32 20V36" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                            <circle cx="32" cy="44" r="2" fill="white"/>
                        </svg>
                    </div>
                    
                    <h2 className="delete-modal-title">Delete Confirmation</h2>
                    
                    <p className="delete-modal-message">
                        Are you sure you want to delete this user
                    </p>
                    
                    <div className="delete-modal-actions">
                        <button 
                            className="delete-confirm-btn" 
                            onClick={confirmDelete}
                            disabled={deleteLoader}
                        >
                            {deleteLoader ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="delete-cancel-btn" 
                            onClick={closeDeleteModal}
                            disabled={deleteLoader}
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </Modal>

            {/* View User Modal */}
            <Modal
                isOpen={isViewModalOpen}
                onRequestClose={closeViewModal}
                contentLabel="View User Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>View User</h2>
                        <p>User basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeViewModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {viewLoader ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>Loading user details...</p>
                    </div>
                ) : viewUserData ? (
                    <div className="form-content view-content">
                        <div className="form-group">
                            <label>User name</label>
                            <div className="view-field-value">{viewUserData.name || 'N/A'}</div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label>Phone number</label>
                                <div className="view-field-value">{viewUserData.phone || 'N/A'}</div>
                            </div>

                            <div className="form-group form-group-half">
                                <label>Email ID</label>
                                <div className="view-field-value">{viewUserData.email || 'N/A'}</div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Address</label>
                            <div className="view-field-value">{viewUserData.address || 'N/A'}</div>
                        </div>

                        <div className="form-group">
                            <label>Role</label>
                            <div className="view-field-value">{viewUserData.role || 'N/A'}</div>
                        </div>

                        <div className="form-group">
                            <label>Status</label>
                            <div className="view-field-value">
                                <span className={`status-badge ${viewUserData.isActive ? 'active' : 'inactive'}`}>
                                    {viewUserData.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>No data available</p>
                    </div>
                )}

                <div className="modal-footer">
                    <button className="submit-btn" onClick={closeViewModal}>
                        Close
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default UserManagement;
